import { Box, Button, Card, CircularProgress, Divider, Grid, Tooltip, Typography } from '@mui/material'
import { IconArrowLeft, IconCheck, IconCopy, IconInfoCircle, IconPercentage } from '@tabler/icons-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { ContentTitle, PageLoader } from '~/components'
import { Input } from '~/components/Form/Input'
import { Radio } from '~/components/Form/Radio'
import { Select } from '~/components/Form/Select'
import { EPartnerStatus, EPersonType, FileUploadInputTypeDto, InputMaybe, PartnerDiscountExceptionCreateDto, PartnerModel, useDealershipListQuery, usePartnerCreateMutation, usePartnerGetQuery, usePartnerGetManagersQuery, usePartnerUpdateMutation } from '~/graphql/types'
import { booleanOptions, partnerStatusOptions, personTypeOptions } from '~/utils/options'
import { DiscountExceptionTable, InitialComissionException, PictureCroped } from './components'
import { useTheme } from '@emotion/react'
import { useNavigate, useParams } from 'react-router-dom'
import { GridColumn } from '~/styles'
import { useCallback, useEffect, useState } from 'react'
import { clearString } from '~/utils'
import * as yup from 'yup'
import { documentSchema, phoneSchema } from '~/utils/yupSchema'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { ErrorText } from '~/components/Form/ErrorText'
import { Checkbox } from '~/components/Form/Checkbox'
import { InitialComissionExceptionData } from './components/InitialComissionException'

export type FormData = {
  personType: EPersonType
  name: string
  code: string
  document: string
  email: string
  phone: string
  site: string
  status: string
  managerId: string | null
  allowFormalizeProposal: boolean
  contractPerson: {
    name: string
    email: string
    phone: string
  }
  hasDiscountsException: boolean
  discountsException: {
    uf: string
    dealershipId: string
    customerPFDiscountMax?: number
    customerPJDiscountMax?: number
    customerPJDiscountSuggested?: number
    customerPFDiscountSuggested?: number
    customerPFDiscountMin?: number
    customerPJDiscountMin?: number
  }[]
  regularLogo?: FileUploadInputTypeDto | null
  reducedLogo?: FileUploadInputTypeDto | null
  hasInitialCommissionException: boolean
  anticipated: {
    isActive: boolean
    commissionedInvoices?: number
    baseCommissionPercentage?: number
    hasCommissionException: boolean
    initialCommissionExceptionData: InitialComissionExceptionData[]
  }
  recurring: {
    isActive: boolean
    alwaysCommissionedInvoices: boolean
    baseCommissionPercentage?: number
    commissionedInvoices?: number
  }
  indicationLinkFull?: string
  indicationLinkReduced?: string
}

export const PartnerCreateEdit: React.FC = () => {
  const { id } = useParams()

  const schema = yup
    .object({
      code: yup.string().required(),
      document: documentSchema,
      email: yup.string().email().required(),
      name: yup.string().required(),
      personType: yup.string().required(),
      phone: phoneSchema,
      site: yup.string().optional(),
      discountsException: yup.array().of(
        yup.object({
          uf: yup.string().optional(),
          dealershipId: yup.string().required(),
          customerPFDiscountMax: yup.number().max(100).required(),
          customerPJDiscountMax: yup.number().max(100).required(),
          customerPJDiscountSuggested: yup.number().max(
            yup.ref('customerPJDiscountMax'),
            'Benefício sugerido para PJ não pode ser maior que o máximo.'
          ).required(),
          customerPFDiscountSuggested: yup.number().max(
            yup.ref('customerPFDiscountMax'),
            'Benefício sugerido para PF não pode ser maior que o máximo.'
          ).required(),
          customerPFDiscountMin: yup.number().max(100).required(),
          customerPJDiscountMin: yup.number().max(100).required(),
        })
      ),
      anticipated: yup.object({
        isActive: yup.boolean(),
        initialCommissionExceptionData: yup.array().of(
          yup.object({
            dealerships: yup.array().of(yup.string()),
            baseCommissionPercentage: yup.number().max(100).required(),
            commissionedInvoices: yup.number().required(),
          })
        ),
        hasCommissionException: yup.boolean(),
        commissionedInvoices: yup.number().when('isActive', {
          is: true,
          then: (validationSchema) => validationSchema.required(),
          otherwise: (validationSchema) => validationSchema.optional()
        }).test('teste', (value, validation) => {
          if (!validation.parent.isActive) {
            return true
          }
          if (!(validation?.parent?.initialCommissionExceptionData && validation.parent.initialCommissionExceptionData.length > 0) && validation.parent.commissionedInvoices === 0) {
            return validation.createError({
              message: 'Este campo só pode ter valor zerado caso possua pelo menos um grupo de exceção de comissão.',
              path: 'anticipated.commissionedInvoices'
            })
          }
          if (validation.parent.commissionedInvoices === 0) {
            return true
          }
          if (validation.parent.baseCommissionPercentage === 0) {
            return validation.createError({
              message: 'Para a comissão base informada, o valor deve ser 0',
              path: 'anticipated.commissionedInvoices'
            })
          }
          return true
        }),
        baseCommissionPercentage: yup.number().when('isActive', {
          is: true,
          then: (validationSchema) => validationSchema.required().max(100),
          otherwise: (validationSchema) => validationSchema.optional()
        }).test('teste', (value, validation) => {
          if (!validation.parent.isActive) {
            return true
          }
          if (!(validation.parent.initialCommissionExceptionData && validation.parent.initialCommissionExceptionData.length > 0) && validation.parent.baseCommissionPercentage === 0) {
            return validation.createError({
              message: 'Este campo só pode ter valor zerado caso possua pelo menos um grupo de exceção de comissão.',
              path: 'anticipated.baseCommissionPercentage'
            })
          }
          if (validation.parent.baseCommissionPercentage === 0) {
            return true
          }
          if (validation.parent.commissionedInvoices === 0) {
            return validation.createError({
              message: 'Para o número de parcelas informado, o valor deve ser 0',
              path: 'anticipated.baseCommissionPercentage'
            })
          }
          return true
        })

      }),
      recurring: yup.object({
        isActive: yup.boolean(),
        alwaysCommissionedInvoices: yup.boolean(),
        baseCommissionPercentage: yup.number().when('isActive', {
          is: true,
          then: (validationSchema) => validationSchema.required().max(100),
          otherwise: (validationSchema) => validationSchema.optional()
        }),
        commissionedInvoices: yup.number().test('isValid', (value, validation) => {
          if (!validation.parent.alwaysCommissionedInvoices && String(value)?.length === 0) {
            return validation.createError({
              message: 'Este campo é obrigatório.',
              path: 'recurring.commissionedInvoices'
            })
          }
          return true
        }),
      }),

      contractPerson: yup.object({
        name: yup.string().required(),
        email: yup.string().email().required(),
        phone: phoneSchema,
      }),

      regularLogo: yup.object({
        fileName: yup.string().optional(),
        fileUrl: yup.string().optional(),
        key: yup.string().optional(),
        storageService: yup.string().optional(),
      }).nullable().default(null)
      ,

      reducedLogo: yup.object({
        fileName: yup.string().optional(),
        fileUrl: yup.string().optional(),
        key: yup.string().optional(),
        storageService: yup.string().optional(),
      }).nullable().default(null)
    }).required()

  const theme = useTheme()
  const navigate = useNavigate()
  const [selectedPartner, setSelectedPartner] = useState<null | PartnerModel>(null)
  const [partnerIsLoading, setPartnerIsLoading] = useState(true)
  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      personType: EPersonType.pf,
      reducedLogo: null,
      regularLogo: null,
      hasDiscountsException: false,
      managerId: '',
      recurring: {
        isActive: false,
        baseCommissionPercentage: 0,
        commissionedInvoices: 0,
        alwaysCommissionedInvoices: false,
      },
      anticipated: {
        isActive: false,
        baseCommissionPercentage: 0,
        commissionedInvoices: 0,
        hasCommissionException: false,
        initialCommissionExceptionData: []
      },
      allowFormalizeProposal: false,
    }
  })

  const {
    personType,
    hasDiscountsException,
    // partnershipConditions,
    discountsException: discountsExceptionWatch,
    anticipated: anticipatedWatch,
    recurring: recurringWatch
  } = useWatch({ control: formMethods.control })

  // const isComissionAnticipated = partnershipConditions?.commissioningType === ECommissioningType.anticipated

  const { data: dealershipList } = useDealershipListQuery({
    variables: {
      params: {
        pageSize: 999999
      }
    }
  })

  const { data: managersData } = usePartnerGetManagersQuery()

  const managerOptions = [
    { label: 'Sem gestor', value: 'Sem gestor' },
    ...(managersData?.partnerGetManagers?.map(manager => ({
      label: manager.name,
      value: manager.id
    })) || [])
  ]

  const { refetch: refetchPartnerGet } = usePartnerGetQuery({
    variables: {
      id: id || ''
    },
    skip: Boolean(!id),
    onCompleted(data) {
      setSelectedPartner(data.partnerGet as PartnerModel)
      setPartnerIsLoading(false)
    },
    onError: (error) => {
      setPartnerIsLoading(false)
      toast.error(error.message)
    }
  })

  const [createPartner, { loading: partnerCreateIsLoading }] = usePartnerCreateMutation({
    onError(error) {
      toast.error(error.message)
    },
    onCompleted() {
      toast.success('O parceiro foi cadastrado!')
      navigate('/app/partners')
    },
  })

  const [updatePartner, { loading: partnerUpdateIsLoading }] = usePartnerUpdateMutation({
    onCompleted() {
      toast.success('O parceiro foi alterado!')
      navigate('/app/partners')
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  useEffect(() => {
    if (id) {
      refetchPartnerGet()
    } else {
      setSelectedPartner(null)
      setPartnerIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (id && selectedPartner && dealershipList && dealershipList?.dealershipList?.data?.length > 0) {
      formMethods.setValue('name', selectedPartner?.name)
      formMethods.setValue('code', selectedPartner?.code || '')
      formMethods.setValue('document', selectedPartner?.document)
      formMethods.setValue('email', selectedPartner?.email)
      formMethods.setValue('phone', selectedPartner?.phone)
      formMethods.setValue('site', selectedPartner?.site || '')
      formMethods.setValue('status', selectedPartner?.status)
      formMethods.setValue('personType', selectedPartner?.personType)
      formMethods.setValue('allowFormalizeProposal', selectedPartner?.allowFormalizeProposal || false)
      formMethods.setValue('managerId', selectedPartner?.manager?.id || 'Sem gestor')

      if (selectedPartner.contactPerson) {
        formMethods.setValue('contractPerson', selectedPartner?.contactPerson)
      }

      if (selectedPartner.partnershipConditions?.recurring?.baseCommissionPercentage) {
        formMethods.setValue('recurring.isActive', true)
        formMethods.setValue('recurring.baseCommissionPercentage', selectedPartner.partnershipConditions.recurring.baseCommissionPercentage || 0)
        formMethods.setValue('recurring.alwaysCommissionedInvoices', selectedPartner.partnershipConditions.recurring.alwaysCommissionedInvoices || false)
        formMethods.setValue('recurring.commissionedInvoices', selectedPartner.partnershipConditions.recurring.commissionedInvoices || 0)
      }

      if (
        selectedPartner.partnershipConditions?.anticipated &&
        (
          selectedPartner.partnershipConditions.anticipated.baseCommissionPercentage > 0 ||
          selectedPartner.partnershipConditions.anticipated.commissionExceptionGroups &&
          selectedPartner.partnershipConditions.anticipated.commissionExceptionGroups.length > 0
        )
      ) {
        formMethods.setValue('anticipated.isActive', true)
        formMethods.setValue('anticipated.baseCommissionPercentage', selectedPartner.partnershipConditions.anticipated.baseCommissionPercentage || 0)
        formMethods.setValue('anticipated.commissionedInvoices', selectedPartner.partnershipConditions.anticipated.commissionedInvoices || 0)
        if (selectedPartner.partnershipConditions.anticipated.commissionExceptionGroups.length > 0) {
          formMethods.setValue('anticipated.hasCommissionException', true)
          formMethods.setValue('anticipated.initialCommissionExceptionData', selectedPartner.partnershipConditions.anticipated.commissionExceptionGroups)
        } else {
          formMethods.setValue('anticipated.hasCommissionException', false)
          formMethods.setValue('anticipated.initialCommissionExceptionData', [])
        }
      }

      if (selectedPartner.discountsException.length > 0) {
        formMethods.setValue('hasDiscountsException', true)
        formMethods.setValue('discountsException', selectedPartner.discountsException.map(item => {
          const uf = dealershipList?.dealershipList.data.find(dealership => dealership._id === item.dealershipId)?.locationUf || ''
          return {
            dealershipId: item.dealershipId,
            uf,
            customerPFDiscountMax: item.customerPFDiscountMax,
            customerPFDiscountMin: item.customerPFDiscountMin,
            customerPFDiscountSuggested: item.customerPFDiscountSuggested,
            customerPJDiscountMax: item.customerPJDiscountMax,
            customerPJDiscountMin: item.customerPJDiscountMin,
            customerPJDiscountSuggested: item.customerPJDiscountSuggested,
          }
        }))
      } else {
        formMethods.setValue('hasDiscountsException', false)
        formMethods.setValue('discountsException', [])
      }
      formMethods.setValue('regularLogo', undefined)

    } else {
      formMethods.reset()
    }
  }, [selectedPartner, id, dealershipList])

  useEffect(() => {
    if (selectedPartner?.code) {
      formMethods.setValue('indicationLinkFull', `${import.meta.env.VITE_APP_SITE_URL}/solicitar-proposta/${selectedPartner?.code}`)
      formMethods.setValue('indicationLinkReduced', `${import.meta.env.VITE_APP_SITE_URL}/nova-proposta/${selectedPartner?.code}`)
    } else {
      formMethods.setValue('indicationLinkFull', '')
      formMethods.setValue('indicationLinkReduced', '')
    }
  }, [formMethods.watch('code')])
  const onSubmit = useCallback((data: FormData) => {

    const commissionExceptionGroups = data.anticipated.hasCommissionException ? data.anticipated.initialCommissionExceptionData.map(item => {
      return {
        baseCommissionPercentage: Number(item.baseCommissionPercentage),
        commissionedInvoices: Number(item.commissionedInvoices),
        dealerships: item.dealerships,
        ...item.id ? { id: item.id } : {}
      }
    }) : []

    const formatedDiscountExecption: PartnerDiscountExceptionCreateDto[] =
      data.hasDiscountsException ? data.discountsException.map(item => ({
        customerPFDiscountMax: item.customerPFDiscountMax || 0,
        customerPFDiscountMin: item.customerPFDiscountMin || 0,
        customerPFDiscountSuggested: item.customerPFDiscountSuggested || 0,
        customerPJDiscountMax: item.customerPJDiscountMax || 0,
        customerPJDiscountMin: item.customerPJDiscountMin || 0,
        customerPJDiscountSuggested: item.customerPJDiscountSuggested || 0,
        dealershipId: item.dealershipId,
      })) : []

    if (id) {

      const logoUpdated: InputMaybe<FileUploadInputTypeDto> | undefined = {
        fileName: data.regularLogo?.fileName || selectedPartner?.configuration.logo?.fileName || '',
        fileUrl: data.regularLogo?.fileUrl || selectedPartner?.configuration.logo?.fileUrl || '',
        key: data.regularLogo?.key || selectedPartner?.configuration.logo?.key || '',
        storageService: data.regularLogo?.storageService || selectedPartner?.configuration.logo?.storageService || '',
      }

      const iconeUpdated: InputMaybe<FileUploadInputTypeDto> | undefined = {
        fileName: data.reducedLogo?.fileName || selectedPartner?.configuration.icone?.fileName || '',
        fileUrl: data.reducedLogo?.fileUrl || selectedPartner?.configuration.icone?.fileUrl || '',
        key: data.reducedLogo?.key || selectedPartner?.configuration.icone?.key || '',
        storageService: data.reducedLogo?.storageService || selectedPartner?.configuration.icone?.storageService || '',
      }

      updatePartner({
        variables: {
          params: {
            partnerId: id || '',
            phone: clearString(data.phone),
            status: data.status as EPartnerStatus,
            email: data.email,
            name: data.name,
            site: data.site.length > 0 ? data.site : null,
            managerId: data.managerId === 'Sem gestor' ? null : data.managerId,
            ...data.code !== selectedPartner?.code ? { code: data.code } : {},
            ...Boolean(data.regularLogo) || Boolean(data.reducedLogo) ? {
              configuration: {
                logo: logoUpdated.fileName ? logoUpdated : null,
                icone: iconeUpdated.fileName ? iconeUpdated : null,
              }
            } : {},
            partnershipConditions: {
              anticipated: data.anticipated.isActive ? {
                commissionExceptionGroups,
                commissionedInvoices: Number(data.anticipated.commissionedInvoices),
                baseCommissionPercentage: data.anticipated.baseCommissionPercentage || 0
              } : null,
              recurring: data.recurring.isActive ? {
                alwaysCommissionedInvoices: data.recurring.alwaysCommissionedInvoices,
                commissionedInvoices: data.recurring.alwaysCommissionedInvoices ? null : Number(data.recurring.commissionedInvoices),
                baseCommissionPercentage: data.recurring.baseCommissionPercentage || 0
              } : null,
            },
            contactPerson: {
              name: data.contractPerson.name,
              email: data.contractPerson.email,
              phone: clearString(data.contractPerson.phone),
            },
            discountsException: formatedDiscountExecption,
            allowFormalizeProposal: data.allowFormalizeProposal
          }
        }
      })
    } else {
      createPartner({
        variables: {
          params: {
            document: clearString(data.document),
            name: data.name,
            email: data.email,
            personType: data.personType,
            status: data.status as EPartnerStatus,
            phone: clearString(data.phone),
            code: data.code,
            site: data.site.length > 0 ? data.site : null,
            managerId: data.managerId === 'Sem gestor' ? null : data.managerId,
            configuration: {
              allowAffiliateLink: true,
              allowManualProposal: true,
              ...data.regularLogo ? { logo: data.regularLogo } : {},
              ...data.reducedLogo ? { icone: data.reducedLogo } : {},
            },
            partnershipConditions: {
              anticipated: data.anticipated.isActive ? {
                commissionExceptionGroups,
                commissionedInvoices: Number(data.anticipated.commissionedInvoices),
                baseCommissionPercentage: data.anticipated.baseCommissionPercentage || 0
              } : null,
              recurring: data.recurring.isActive ? {
                alwaysCommissionedInvoices: data.recurring.alwaysCommissionedInvoices,
                commissionedInvoices: data.recurring.alwaysCommissionedInvoices ? null : Number(data.recurring.commissionedInvoices),
                baseCommissionPercentage: data.recurring.baseCommissionPercentage || 0
              } : null,
            },
            contactPerson: {
              email: data.contractPerson.email,
              name: data.contractPerson.name,
              phone: clearString(data.contractPerson.phone),
            },
            discountsException: formatedDiscountExecption,
            allowFormalizeProposal: data.allowFormalizeProposal
          }
        }
      })
    }

  }, [id, selectedPartner])

  const partnerImages = {
    logo: selectedPartner?.code && selectedPartner.configuration.logo?.fileName ? `${process.env.VITE_APP_REST_API_URL}partners/logo/${selectedPartner?.code}` : null,
    icone: selectedPartner?.code && selectedPartner.configuration.icone?.fileName ? `${process.env.VITE_APP_REST_API_URL}partners/icone/${selectedPartner?.code}` : null
  }

  const canRemoveException = Boolean(discountsExceptionWatch && discountsExceptionWatch?.length > 0)

  const isValidForm = formMethods.formState.isValid

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copiado para a área de transferência!')
    }).catch(() => {
      toast.error('Erro ao copiar link')
    })
  }

  if (partnerIsLoading) return <PageLoader />

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem 1rem 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title={id ? 'Editar representante' : 'Cadastrar representante'}
          description={id ? 'Edição e gestão de representantes comerciais da Desperta' : 'Cadastro e gestão de representantes comerciais da Desperta'}
          breadcrumbLinks={{ currentLink: id ? 'Editar representante' : 'Cadastrar representante', links: [{ href: '/app/partners', label: 'Representantes' }] }}
        />
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit((data) => onSubmit(data))}>
            <Box>
              <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, paddingBottom: '1rem' }} variant='h3'>Personalização da plataforma</Typography>
              <GridColumn columns={2} responsive={[{ breakWidth: theme.breakpoints.values.md, columns: 1 }]}>
                <Box>
                  <PictureCroped partnerLogo={partnerImages.logo} typeLogo='defaultLogo' />
                  <ErrorText $margin='-20px 0 0 0' error={formMethods.formState.errors.regularLogo ? 'Marca regular é obrigatório' : undefined} />
                </Box>
                <Box>
                  <PictureCroped partnerLogo={partnerImages.icone} typeLogo='reducedLogo' />
                  <ErrorText $margin='-20px 0 0 0' error={formMethods.formState.errors.reducedLogo ? 'Marca reduzida é obrigatório' : undefined} />
                </Box>
              </GridColumn>
            </Box>
            {/* Links de indicação */}
            {id && (
              <>
                <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px', padding: '1rem 0 .4rem 0' }}>Links de indicação do representante</Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography color={theme.palette.grey[400]} sx={{ fontSize: '14px', marginBottom: '0.5rem' }}>Página completa</Typography>
                    <Input
                      readOnly
                      disabled={false}
                      inputProps={{ readOnly: true, style: { cursor: 'text' } }}
                      name='indicationLinkFull'
                      icons={{
                        end: {
                          element: (
                            <Tooltip title='Copiar link'>
                              <Button
                                onClick={() => copyToClipboard(formMethods.getValues('indicationLinkFull') || '')}
                                sx={{ minWidth: 'auto', border: 'none', padding: '0.5rem', display: 'flex', alignItems: 'center' }}
                                color='secondary'
                              >
                                <IconCopy size={20} />
                              </Button>
                            </Tooltip>
                          )
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography color={theme.palette.grey[400]} sx={{ fontSize: '14px', marginBottom: '0.5rem' }}>Página reduzida (apenas formulário)</Typography>
                    <Input
                      readOnly
                      disabled={false}
                      inputProps={{ readOnly: true, style: { cursor: 'text' } }}
                      name='indicationLinkReduced'
                      icons={{
                        end: {
                          element: (
                            <Tooltip title='Copiar link'>
                              <Button
                                onClick={() => copyToClipboard(formMethods.getValues('indicationLinkReduced') || '')}
                                sx={{ minWidth: 'auto', border: 'none', padding: '0.5rem', display: 'flex', alignItems: 'center' }}
                                color='secondary'
                              >
                                <IconCopy size={20} />
                              </Button>
                            </Tooltip>
                          )
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            {/* fim links de indicação */}

            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '24px', padding: '1rem 0 .4rem 0' }}>Tipo de pessoa:</Typography>
            <Radio disabled={Boolean(id)} row name='personType' options={personTypeOptions} />

            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '24px', padding: '1rem 0 .4rem 0' }}>Dados básicos:</Typography>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={4} md={4}>
                <Input name='name' label='Nome do parceiro' />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Input name='code' label='Código do parceiro' />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Input disabled={Boolean(id)} mask={personType === EPersonType.pf ? 'cpf' : 'cnpj'} name='document' label={personType === EPersonType.pf ? 'CPF' : 'CNPJ'} />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Input name='email' label='E-mail' />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Input mask='phone' name='phone' label='Telefone' />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Input name='site' label='Site (Opcional)' />
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <Select name='managerId' label='Gestor' options={managerOptions} hideDefaultOption />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Select name='status' label='Status' options={partnerStatusOptions} />
              </Grid>

            </Grid>

            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px', padding: '1rem 0 .4rem 0' }}>Dados do representante:</Typography>

            <Grid spacing={2} container>
              <Grid item xs={12} sm={12} md={8}>
                <Input name='contractPerson.name' label='Nome completo' />
              </Grid>
              <Grid sx={{ padding: '0 !important' }} item xs={12} sm={12} md={12} />
              <Grid item xs={12} sm={6} md={4}>
                <Input name='contractPerson.email' label='E-mail' />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Input mask='phone' name='contractPerson.phone' label='Telefone' />
              </Grid>
            </Grid>

            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '24px', padding: '1.6rem 0 .4rem 0' }}>Formalização de propostas</Typography>
            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px', padding: '0 0 .2rem 0' }}>Este parceiro pode formalizar propostas em sua área?</Typography>
            <Grid sx={{ marginBottom: '1.2rem' }} container>
              <Grid item xs={12}>
                <Radio row options={[{ label: 'Sim', value: true }, { label: 'Não', value: false }]} name='allowFormalizeProposal' />
              </Grid>
            </Grid>

            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '24px', padding: '1rem 0 .4rem 0' }}>Comissionamento</Typography>
            <Grid sx={{ marginBottom: '1.2rem' }} container>
              <Grid item xs={12}>
                <Checkbox name='recurring.isActive' label='Recorrente' />
              </Grid>
            </Grid>

            {recurringWatch?.isActive && (
              <>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={6} md={4}>
                    <Input icons={{ end: { element: <IconPercentage /> } }} mask='percentage' name='recurring.baseCommissionPercentage' label='Percentual de comissão base' />
                  </Grid>
                </Grid>

                <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px', padding: '1.4rem 0 .4rem 0' }}>Tem número ilimitado de faturas comissionadas?</Typography>
                <Radio useDefaultValue={Boolean(id)} row name='recurring.alwaysCommissionedInvoices' options={booleanOptions} />

                {!recurringWatch.alwaysCommissionedInvoices && (
                  <Grid spacing={2} container>
                    <Grid item xs={12} sm={6} md={4}>
                      <Input type='number' name='recurring.commissionedInvoices' label='Número de faturas comissionadas' />
                    </Grid>
                  </Grid>
                )}

                <Divider sx={{ margin: '1.2rem 0' }} />
              </>
            )}

            <Grid sx={{ marginBottom: '1.2rem' }} container>
              <Grid item xs={12}>
                <Checkbox name='anticipated.isActive' label='Inicial' />
              </Grid>
            </Grid>

            {anticipatedWatch?.isActive && (
              <>
                <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px', padding: '0 0 1rem 0' }}>Regra geral</Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={6} md={4}>
                    <Input
                      icons={{
                        end: {
                          element:
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                              <div style={{ paddingTop: '.3rem' }}>
                                <Tooltip title='Porcentagem da fatura a ser paga em cada uma das parcelas'>
                                  <IconInfoCircle color={theme.palette.info.dark} />
                                </Tooltip>
                              </div>
                              <IconPercentage />
                            </div>
                        }
                      }}
                      mask='percentage'
                      name='anticipated.baseCommissionPercentage'
                      label='Percentual de comissão base'
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Input
                      type='number'
                      name='anticipated.commissionedInvoices'
                      label='Número de parcelas'
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ margin: '1.2rem 0' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                  <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px' }}>Exceção de comissão inicial?</Typography>
                  <Tooltip title='Crie grupos com regras de comissão inicial diferentes da geral'>
                    <IconInfoCircle color={theme.palette.info.dark} />
                  </Tooltip>
                </Box>
                <Radio useDefaultValue={Boolean(id)} row name='anticipated.hasCommissionException' options={booleanOptions} />
                {anticipatedWatch?.hasCommissionException &&
                  <InitialComissionException dealerships={dealershipList?.dealershipList.data || []} />
                }
              </>
            )}

            <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '24px', padding: '1rem 0 .4rem 0' }}>Benefício do cliente</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
              <Typography color={theme.palette.grey[800]} sx={{ fontWeight: 500, fontSize: '16px', padding: '.2rem 0 .4rem 0' }}>Exceção ao benefício de concessionárias?</Typography>
              <Tooltip title='Indica se o parceiro pode oferecer % de benefício diferente do cadastrado na concessionária ao cliente'>
                <IconInfoCircle color={theme.palette.info.dark} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gpa: '1rem', alignItems: 'center' }}>
              <Radio disabled={canRemoveException} useDefaultValue={Boolean(id)} row name='hasDiscountsException' options={booleanOptions} />
              {canRemoveException && (
                <Tooltip title='Para poder alterar a exceção de benefício, é preciso remover todas as concessionárias cadastradas'>
                  <IconInfoCircle color={theme.palette.info.dark} />
                </Tooltip>
              )}
            </Box>

            {hasDiscountsException &&
              <DiscountExceptionTable selectedPartner={selectedPartner} dealerships={dealershipList?.dealershipList.data || []} />
            }

            <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '2rem 0 1rem 0', gap: '1rem' }}>
              <Button onClick={() => navigate('/app/partners')} color='secondary'>
                <IconArrowLeft />
                Voltar
              </Button>
              {!isValidForm ? (
                <Box>
                  <Tooltip title='Preencha todos os campos obrigatórios'>
                    <Button disabled={false} type='submit'>
                      <IconCheck />
                      {id ? 'Editar parceiro' : 'Confirmar cadastro'}
                    </Button>
                  </Tooltip>
                </Box>
              ) : (
                <Button
                  endIcon={partnerCreateIsLoading || partnerUpdateIsLoading ? <CircularProgress size={24} color='inherit' /> : null}
                  disabled={partnerCreateIsLoading || partnerUpdateIsLoading} type='submit'>
                  <IconCheck
                  />
                  {id ? 'Editar parceiro' : 'Confirmar cadastro'}
                </Button>
              )}
            </Box>
          </form>
        </FormProvider>

      </Card>
    </div>
  )
}
